import React, { useState } from "react";
import { useGetUserQuery } from "../services/userService";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit,
  Person,
  CheckCircle,
  Cancel,
  Phone,
} from "@mui/icons-material";
import UserFormModal from "../components/UserFormModal";
import toast from "react-hot-toast";

const User = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { data: user, error, isFetching, refetch } = useGetUserQuery(id as string);

  const handleEditSuccess = () => {
    refetch();
    setIsEditModalOpen(false);
    toast.success("User updated successfully");
  };

  if (isFetching)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading user data. Please try again later.
      </Alert>
    );

  if (!user) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        User not found
      </Alert>
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          User Details
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Edit />}
          sx={{ ml: "auto" }}
          onClick={() => setIsEditModalOpen(true)}
        >
          Edit Profile
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/** Left Column - Profile Info */}
        <Grid>
          <Card>
            <CardContent sx={{ textAlign: "center", pt: 4 }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  margin: "0 auto 16px",
                  bgcolor: "primary.main",
                }}
              >
                <Person sx={{ fontSize: 60 }} />
              </Avatar>
              <Typography variant="h5" component="div" gutterBottom>
                {user.name}
              </Typography>
              <Chip
                label={user.role}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                {user.isActive ? (
                  <Chip
                    icon={<CheckCircle />}
                    label="Active"
                    color="success"
                    size="small"
                  />
                ) : (
                  <Chip
                    icon={<Cancel />}
                    label="Active"
                    color="error"
                    size="small"
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardHeader title="Contact Information" />
            <CardContent>
              <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                <Phone color="action" sx={{ mr: 2 }} />
                <Typography>{user.phone || "N/A"}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/** Right Column - Additional Info */}
        <Grid>
          <Card>
            <CardHeader title="Account Info" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid>
                  <Typography variant="subtitle2" color="textSecondary">
                    User ID
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {user._id}
                  </Typography>
                </Grid>

                <Grid>
                  <Typography variant="subtitle2" color="textSecondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(user.updatedAt)}
                  </Typography>
                </Grid>
                <Grid>
                  <Typography variant="subtitle2" color="textSecondary">
                    Last Login
                  </Typography>
                  <Typography variant="body1">
                    {user.lastLogin
                      ? formatDate(user.lastLogin)
                      : "Never logged in"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit User Modal */}
      {user && (
        <UserFormModal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={handleEditSuccess}
          user={user}
        />
      )}
    </Box>
  );
};

export default User;
